import {computed, observable} from 'mobx';

import {IMCMessage} from '../../api/proto';
import {TELEGRAM_MESSAGE_EDIT_WINDOW} from '../../config/telegram';
import {WHATSAPP_MESSAGE_EDIT_WINDOW} from '../../config/whatsapp';
import fromUnixStamp from '../../utils/time/fromUnixStamp';
import MessageAttachment from '../Attachment/MessageAttachment';
import Chat from '../Chat';
import Peer from '../Peer';
import MessageBase from './MessageBase';
import {getMessageServiceText} from './utils';

export type MessageClickEventHandler<T = Message> = (message: T) => void;

export class Message extends MessageBase {
  constructor(raw: IMCMessage, chat: Chat) {
    super(raw, chat);
    this.update(raw);
  }

  @observable messageNode: HTMLElement | null = null;

  setMessageRef = (ref: HTMLElement | null) => {
    this.messageNode = ref;
  };

  @computed get isDelivered(): boolean {
    return !this.sending;
  }

  @computed get menuIsAvailable(): boolean {
    return this.isDelivered;
  }

  @computed get isSelected(): boolean {
    return false; //NOT implemented yet
  }

  @computed get isReadInbox(): boolean {
    return !(!this.chat?.inReadPointer || this.localID?.greaterThan(this.chat.inReadPointer));
  }

  @computed get isReadOutbox(): boolean {
    return !(!this.chat?.outReadPointer || this.localID?.greaterThan(this.chat.outReadPointer));
  }

  @computed get isEmpty(): boolean {
    return !this.text
      && !this.attachments?.length
      && !this.replyToMessageID
      && !this.serviceMessage
      && !this.groupID
      ;
  }

  @computed get daysPassed() {
    const diff = new Date().getTime() - fromUnixStamp(this.stamp);
    return Math.floor(diff / (24 * 60 * 60 * 1000));
  }

  @computed get minutesPassed() {
    const diff = new Date().getTime() - fromUnixStamp(this.stamp);
    return Math.floor(diff / (60 * 1000));
  }

  @computed get editingIsAvailable() {
    if (!this.isOwn || this.deleted) {
      return false;
    }

    if (this.chat.channel.isTelegram) {
      return this.daysPassed < TELEGRAM_MESSAGE_EDIT_WINDOW;
    } else if (this.chat.channel.isWebWidget) {
      return true;
    } else if (this.chat.channel.isWhatsapp) {
      return this.minutesPassed < WHATSAPP_MESSAGE_EDIT_WINDOW;
    }

    return false;
  }

  update = (props: IMCMessage) => {
    this.assign_(this, {
      ...props,
      attachments: props.attachments?.map((attach) => new MessageAttachment(attach, this)),
    });
    Object.assign(this.raw, props);
  };

  navigateToMessage = (delay?: number) => {
    if (delay) {
      setTimeout(() => {
        this.chat.store?.navigateToMessage(this);
      }, delay);
    } else {
      this.chat.store?.navigateToMessage(this);
    }
  };

  @computed get serviceMessageText(): string {
    return this.serviceMessage ? getMessageServiceText(this.serviceMessage, this) : '';
  }

  @computed get hidden(): boolean {
    //Hide by backend dev's request, because have  the same message [migratedFromSuperGroup]
    return !!this.serviceMessage?.migratedToSuperGroup;
  }

  @computed get peerInstance(): Peer | null {
    return this.peer ? new Peer(this.peer, this.chat) : null;
  }
}

export default Message;
